import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Download, 
  Check, 
  TrendingUp, 
  DollarSign, 
  Hand,
  Brain,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  FileText,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Zap,
  ShoppingCart,
  Plus,
  Upload,
  RefreshCw,
  X
} from "lucide-react";
import Navbar from "../components/Navbar";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { saveAs } from "file-saver";

const ComparisonResults = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectionMethod, setSelectionMethod] = useState(null);
  const [expandedAnalysis, setExpandedAnalysis] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [selecting, setSelecting] = useState(false);
  
  // Add vendor state
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [newVendorFile, setNewVendorFile] = useState(null);
  const [addingVendor, setAddingVendor] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  
  // NEW: Line item selection state
  // Format: { "item_name": { vendorId: "vendor_123", checked: true } }
  const [selectedItems, setSelectedItems] = useState({});
  const [selectionMode, setSelectionMode] = useState("manual"); // "manual", "lowest", "vendor"

  const fetchComparison = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/comparisons/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setComparison(response.data);
      if (response.data.selected_vendor_id) {
        setSelectedVendor(response.data.selected_vendor_id);
        setSelectionMethod(response.data.selection_method);
      }
    } catch (error) {
      console.error("Error fetching comparison:", error);
      toast.error("Failed to load comparison");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComparison();
  }, [id]);

  // Dropzone for add vendor modal
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setNewVendorFile(acceptedFiles[0]);
      if (!newVendorName) {
        setNewVendorName(acceptedFiles[0].name.replace(/\.[^/.]+$/, ""));
      }
    }
  }, [newVendorName]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/*': ['.png', '.jpg', '.jpeg', '.webp']
    },
    multiple: false
  });

  // Add new vendor to comparison
  const handleAddVendor = async () => {
    if (!newVendorFile || !newVendorName.trim()) {
      toast.error("Please provide vendor name and file");
      return;
    }

    setAddingVendor(true);
    try {
      const token = localStorage.getItem("auth_token");
      const formData = new FormData();
      formData.append("file", newVendorFile);

      await axios.post(
        `${API}/comparisons/${id}/vendors?vendor_name=${encodeURIComponent(newVendorName)}`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          withCredentials: true
        }
      );

      toast.success(`${newVendorName} added successfully!`);
      setShowAddVendorModal(false);
      setNewVendorName("");
      setNewVendorFile(null);
      
      // Re-run matching with new vendor
      await handleReanalyze();
    } catch (error) {
      console.error("Error adding vendor:", error);
      toast.error(error.response?.data?.detail || "Failed to add vendor");
    } finally {
      setAddingVendor(false);
    }
  };

  // Re-analyze/re-match all vendors
  const handleReanalyze = async () => {
    setReanalyzing(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(
        `${API}/comparisons/${id}/match`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      // Clear selections since items may have changed
      setSelectedItems({});
      
      // Refresh comparison data
      await fetchComparison();
      toast.success("Analysis updated with all vendors!");
    } catch (error) {
      console.error("Error re-analyzing:", error);
      toast.error("Failed to re-analyze comparison");
    } finally {
      setReanalyzing(false);
    }
  };

  // Calculate total based on selected items
  const selectionSummary = useMemo(() => {
    const vendors = comparison?.vendors || [];
    const matchedItems = comparison?.matched_items || [];
    
    let totalPrice = 0;
    let itemCount = 0;
    const itemsByVendor = {};
    
    Object.entries(selectedItems).forEach(([itemName, selection]) => {
      if (!selection.checked) return;
      
      const item = matchedItems.find(i => i.item_name === itemName);
      if (!item) return;
      
      const vendorData = item.vendors[selection.vendorId];
      if (!vendorData) return;
      
      const price = vendorData.unit_price || vendorData.total_price || 0;
      const quantity = vendorData.quantity || 1;
      const lineTotal = price * quantity;
      
      totalPrice += lineTotal;
      itemCount++;
      
      const vendor = vendors.find(v => v.vendor_id === selection.vendorId);
      const vendorName = vendor?.name || "Unknown";
      
      if (!itemsByVendor[vendorName]) {
        itemsByVendor[vendorName] = { items: [], subtotal: 0 };
      }
      itemsByVendor[vendorName].items.push({
        name: itemName,
        price,
        quantity,
        total: lineTotal
      });
      itemsByVendor[vendorName].subtotal += lineTotal;
    });
    
    return { totalPrice, itemCount, itemsByVendor };
  }, [selectedItems, comparison]);

  // Select all lowest price items
  const selectAllLowest = () => {
    const matchedItems = comparison?.matched_items || [];
    const newSelection = {};
    
    matchedItems.forEach(item => {
      if (item.lowest_vendor_id) {
        newSelection[item.item_name] = {
          vendorId: item.lowest_vendor_id,
          checked: true
        };
      }
    });
    
    setSelectedItems(newSelection);
    setSelectionMode("lowest");
    toast.success("Selected all lowest price items");
  };

  // Select all items from a specific vendor
  const selectAllFromVendor = (vendorId) => {
    const matchedItems = comparison?.matched_items || [];
    const vendors = comparison?.vendors || [];
    const newSelection = {};
    
    matchedItems.forEach(item => {
      if (item.vendors[vendorId]) {
        newSelection[item.item_name] = {
          vendorId: vendorId,
          checked: true
        };
      }
    });
    
    setSelectedItems(newSelection);
    setSelectionMode("vendor");
    const vendorName = vendors.find(v => v.vendor_id === vendorId)?.name;
    toast.success(`Selected all items from ${vendorName}`);
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedItems({});
    setSelectionMode("manual");
  };

  // Toggle individual item selection
  const toggleItemSelection = (itemName, vendorId, checked) => {
    setSelectedItems(prev => {
      const newSelection = { ...prev };
      
      if (checked) {
        newSelection[itemName] = { vendorId, checked: true };
      } else {
        delete newSelection[itemName];
      }
      
      return newSelection;
    });
    setSelectionMode("manual");
  };

  // Check if an item-vendor combination is selected
  const isItemSelected = (itemName, vendorId) => {
    return selectedItems[itemName]?.vendorId === vendorId && selectedItems[itemName]?.checked;
  };

  const handleSelectVendor = async (vendorId, method) => {
    setSelecting(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(
        `${API}/comparisons/${id}/select?vendor_id=${vendorId}&method=${method}`,
        {},
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true 
        }
      );
      setSelectedVendor(vendorId);
      setSelectionMethod(method);
      toast.success("Vendor selected successfully!");
    } catch (error) {
      toast.error("Failed to select vendor");
    } finally {
      setSelecting(false);
    }
  };

  const autoSelectByScore = () => {
    const vendors = comparison?.vendors || [];
    const bestVendor = vendors.reduce((best, current) => {
      const currentScore = current.recommendation_score || current.ai_analysis?.recommendation_score || 0;
      const bestScore = best?.recommendation_score || best?.ai_analysis?.recommendation_score || 0;
      return currentScore > bestScore ? current : best;
    }, null);
    
    if (bestVendor) {
      handleSelectVendor(bestVendor.vendor_id, "score");
    }
  };

  const autoSelectByPrice = () => {
    const vendors = comparison?.vendors || [];
    const cheapest = vendors.reduce((best, current) => {
      const currentPrice = current.total_price || Infinity;
      const bestPrice = best?.total_price || Infinity;
      return currentPrice < bestPrice ? current : best;
    }, null);
    
    if (cheapest) {
      handleSelectVendor(cheapest.vendor_id, "price");
    }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(
        `${API}/comparisons/${id}/export?format=${format}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
          responseType: "blob"
        }
      );
      
      // Check if response is an error (blob could be error JSON)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Export failed');
      }
      
      const blob = new Blob([response.data], {
        type: format === "xlsx" 
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv"
      });
      
      // Use comparison name, sanitized for filename
      const fileName = (comparison?.name || 'comparison').replace(/[^a-zA-Z0-9]/g, '_');
      saveAs(blob, `${fileName}_comparison.${format}`);
      toast.success(`Exported as ${format.toUpperCase()}`);
      setShowExportModal(false);
    } catch (error) {
      console.error("Export error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Export failed";
      toast.error(errorMessage);
    }
  };

  // Export selected items only
  const exportSelection = () => {
    const { itemsByVendor, totalPrice } = selectionSummary;
    
    let text = "Selected Items Summary\n";
    text += "=".repeat(50) + "\n\n";
    
    Object.entries(itemsByVendor).forEach(([vendorName, data]) => {
      text += `Vendor: ${vendorName}\n`;
      text += "-".repeat(30) + "\n";
      data.items.forEach(item => {
        text += `${item.name}\tQty: ${item.quantity}\t$${item.price.toLocaleString()}\t= $${item.total.toLocaleString()}\n`;
      });
      text += `Subtotal: $${data.subtotal.toLocaleString()}\n\n`;
    });
    
    text += "=".repeat(50) + "\n";
    text += `GRAND TOTAL: $${totalPrice.toLocaleString()}\n`;
    
    navigator.clipboard.writeText(text);
    toast.success("Selection copied to clipboard!");
  };

  const copyToClipboard = () => {
    const vendors = comparison?.vendors || [];
    const matchedItems = comparison?.matched_items || [];
    
    let text = "Item Name\tQuantity\t" + vendors.map(v => v.name).join("\t") + "\tLowest\tSelected\n";
    
    matchedItems.forEach(item => {
      let row = item.item_name + "\t";
      
      // Get quantity from first vendor that has it
      const firstVendorData = Object.values(item.vendors)[0];
      const qty = firstVendorData?.quantity || 1;
      row += qty + "\t";
      
      vendors.forEach(vendor => {
        const vendorData = item.vendors[vendor.vendor_id];
        const price = vendorData?.unit_price || vendorData?.total_price || "-";
        row += price + "\t";
      });
      
      const lowestVendor = vendors.find(v => v.vendor_id === item.lowest_vendor_id);
      row += (lowestVendor?.name || "-") + "\t";
      
      // Add selected vendor
      const selectedInfo = selectedItems[item.item_name];
      if (selectedInfo?.checked) {
        const selectedVendorName = vendors.find(v => v.vendor_id === selectedInfo.vendorId)?.name || "-";
        row += selectedVendorName;
      } else {
        row += "-";
      }
      
      text += row + "\n";
    });
    
    text += `\nTotal Selected: $${selectionSummary.totalPrice.toLocaleString()}`;
    
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard! Paste into Excel.");
    setShowExportModal(false);
  };

  const toggleAnalysis = (vendorId) => {
    setExpandedAnalysis(prev => ({
      ...prev,
      [vendorId]: !prev[vendorId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020408] flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full border-2 border-primary-main flex items-center justify-center">
          <div className="w-8 h-8 rounded-full bg-primary-main/30" />
        </div>
      </div>
    );
  }

  const vendors = comparison?.vendors || [];
  const matchedItems = comparison?.matched_items || [];

  return (
    <div className="min-h-screen bg-[#020408]">
      <Navbar user={user} />

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
            <div>
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="mb-4 text-gray-400 hover:text-white -ml-3"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="font-display text-4xl font-bold tracking-tight mb-2">
                {comparison?.name}
              </h1>
              <p className="text-gray-400">
                {comparison?.description || `Comparing ${vendors.length} vendors`}
              </p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {/* Add Vendor Button */}
              <Button
                data-testid="add-vendor-btn"
                variant="outline"
                onClick={() => setShowAddVendorModal(true)}
                className="border-accent-success text-accent-success hover:bg-accent-success/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Vendor
              </Button>

              {/* Re-analyze Button */}
              <Button
                data-testid="reanalyze-btn"
                variant="outline"
                onClick={handleReanalyze}
                disabled={reanalyzing}
                className="border-secondary-main text-secondary-main hover:bg-secondary-main/10"
              >
                {reanalyzing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {reanalyzing ? "Analyzing..." : "Re-analyze"}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="auto-select-btn"
                    variant="outline"
                    className="border-primary-main text-primary-main hover:bg-primary-main/10"
                    disabled={selecting}
                  >
                    {selecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Auto Select
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-background-paper border-border">
                  <DropdownMenuItem 
                    onClick={autoSelectByScore}
                    className="cursor-pointer hover:bg-background-subtle"
                    data-testid="select-by-score"
                  >
                    <TrendingUp className="w-4 h-4 mr-2 text-accent-success" />
                    Highest AI Score
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={autoSelectByPrice}
                    className="cursor-pointer hover:bg-background-subtle"
                    data-testid="select-by-price"
                  >
                    <DollarSign className="w-4 h-4 mr-2 text-accent-warning" />
                    Lowest Total Price
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button
                data-testid="export-btn"
                onClick={() => setShowExportModal(true)}
                className="btn-skew bg-primary-main text-black font-bold uppercase tracking-wider px-6 hover:bg-primary-light hover:shadow-glow-cyan transition-all"
              >
                <span className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export
                </span>
              </Button>
            </div>
          </div>

          {/* Selection Status */}
          {selectedVendor && (
            <div className="mb-8 p-4 glass rounded-xl border-accent-success/30 bg-accent-success/5">
              <div className="flex items-center gap-3">
                <Check className="w-6 h-6 text-accent-success" />
                <div>
                  <span className="font-semibold">
                    Selected: {vendors.find(v => v.vendor_id === selectedVendor)?.name}
                  </span>
                  <span className="text-gray-400 ml-2 text-sm">
                    (by {selectionMethod === "score" ? "highest score" : selectionMethod === "price" ? "lowest price" : "manual selection"})
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* LINE ITEM SELECTION CONTROLS */}
          <div className="mb-8 glass rounded-xl p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-semibold mb-1">Line Item Selection</h2>
                <p className="text-gray-400 text-sm">
                  Select items from different vendors to build your optimal order
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Button
                  data-testid="select-lowest-btn"
                  onClick={selectAllLowest}
                  variant="outline"
                  className="border-accent-success text-accent-success hover:bg-accent-success/10"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Select All Lowest
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-border hover:bg-background-subtle"
                      data-testid="select-vendor-dropdown"
                    >
                      Select by Vendor
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-background-paper border-border">
                    {vendors.map(vendor => (
                      <DropdownMenuItem 
                        key={vendor.vendor_id}
                        onClick={() => selectAllFromVendor(vendor.vendor_id)}
                        className="cursor-pointer hover:bg-background-subtle"
                      >
                        {vendor.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <Button
                  variant="ghost"
                  onClick={clearSelection}
                  className="text-gray-400 hover:text-white"
                  data-testid="clear-selection-btn"
                >
                  Clear All
                </Button>
              </div>
            </div>
          </div>

          {/* SELECTION SUMMARY CARD */}
          {selectionSummary.itemCount > 0 && (
            <div className="mb-8 glass rounded-xl p-6 border-primary-main/30 animate-fade-in">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <ShoppingCart className="w-6 h-6 text-primary-main" />
                    <h2 className="font-display text-xl font-semibold">Your Selection</h2>
                    <span className="px-2 py-0.5 text-xs font-mono uppercase bg-primary-main/20 text-primary-main rounded">
                      {selectionSummary.itemCount} items
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    {Object.entries(selectionSummary.itemsByVendor).map(([vendorName, data]) => (
                      <div key={vendorName} className="pl-4 border-l-2 border-primary-main/30">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-200">{vendorName}</span>
                          <span className="font-mono text-sm text-gray-400">
                            ${data.subtotal.toLocaleString()}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {data.items.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm text-gray-400">
                              <span>{item.name} × {item.quantity}</span>
                              <span>${item.total.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">Grand Total</div>
                  <div className="font-display text-4xl font-bold text-primary-main neon-cyan">
                    ${selectionSummary.totalPrice.toLocaleString()}
                  </div>
                  <Button
                    onClick={exportSelection}
                    className="mt-4 bg-primary-main/20 text-primary-main hover:bg-primary-main/30"
                    data-testid="copy-selection-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Copy Selection
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Vendors Grid with AI Analysis */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {vendors.map((vendor) => {
              const analysis = vendor.ai_analysis || {};
              const score = vendor.recommendation_score || analysis.recommendation_score || 0;
              const isSelected = selectedVendor === vendor.vendor_id;
              const isExpanded = expandedAnalysis[vendor.vendor_id];
              
              return (
                <div
                  key={vendor.vendor_id}
                  data-testid={`vendor-analysis-${vendor.vendor_id}`}
                  className={`glass rounded-xl overflow-hidden transition-all ${
                    isSelected ? "ring-2 ring-accent-success" : "hover:border-primary-main/30"
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-display text-xl font-semibold">{vendor.name}</h3>
                        <p className="text-sm text-gray-500">{vendor.file_name}</p>
                      </div>
                      {isSelected && (
                        <span className="px-2 py-1 text-xs font-mono uppercase bg-accent-success/20 text-accent-success rounded">
                          Selected
                        </span>
                      )}
                    </div>
                    
                    {/* Score */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-400">AI Score</span>
                          <span className={`font-mono font-bold ${
                            score >= 70 ? "text-accent-success" : 
                            score >= 50 ? "text-accent-warning" : "text-red-400"
                          }`}>
                            {score}/100
                          </span>
                        </div>
                        <div className="h-2 bg-background-subtle rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-500 ${
                              score >= 70 ? "bg-accent-success" : 
                              score >= 50 ? "bg-accent-warning" : "bg-red-400"
                            }`}
                            style={{ width: `${score}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Total Price */}
                    {vendor.total_price && (
                      <div className="mb-4 p-3 bg-background-subtle rounded-lg">
                        <span className="text-sm text-gray-400">Total Price</span>
                        <div className="font-display text-2xl font-bold text-primary-main">
                          ${vendor.total_price.toLocaleString()}
                        </div>
                      </div>
                    )}
                    
                    {/* Quick Pros/Cons */}
                    <div className="space-y-3 mb-4">
                      {analysis.pros?.slice(0, 2).map((pro, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-accent-success flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">{pro}</span>
                        </div>
                      ))}
                      {analysis.cons?.slice(0, 1).map((con, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">{con}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Expand/Collapse */}
                    <Button
                      variant="ghost"
                      onClick={() => toggleAnalysis(vendor.vendor_id)}
                      className="w-full text-gray-400 hover:text-white hover:bg-background-subtle"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-2" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-2" />
                          View Full Analysis
                        </>
                      )}
                    </Button>
                    
                    {/* Expanded Analysis */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-border space-y-4 animate-fade-in">
                        {analysis.summary && (
                          <div>
                            <h4 className="font-semibold text-sm text-primary-main mb-2 flex items-center gap-2">
                              <Brain className="w-4 h-4" />
                              AI Summary
                            </h4>
                            <p className="text-sm text-gray-300">{analysis.summary}</p>
                          </div>
                        )}
                        
                        {vendor.scope_of_work && (
                          <div>
                            <h4 className="font-semibold text-sm text-gray-400 mb-1">Scope of Work</h4>
                            <p className="text-sm text-gray-300">{vendor.scope_of_work}</p>
                          </div>
                        )}
                        
                        {vendor.payment_terms && (
                          <div>
                            <h4 className="font-semibold text-sm text-gray-400 mb-1">Payment Terms</h4>
                            <p className="text-sm text-gray-300">{vendor.payment_terms}</p>
                          </div>
                        )}
                        
                        {vendor.delivery_terms && (
                          <div>
                            <h4 className="font-semibold text-sm text-gray-400 mb-1">Delivery Terms</h4>
                            <p className="text-sm text-gray-300">{vendor.delivery_terms}</p>
                          </div>
                        )}
                        
                        {analysis.pros?.length > 2 && (
                          <div>
                            <h4 className="font-semibold text-sm text-accent-success mb-2">All Pros</h4>
                            <ul className="space-y-1">
                              {analysis.pros.map((pro, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm">
                                  <Check className="w-3 h-3 text-accent-success flex-shrink-0 mt-1" />
                                  <span className="text-gray-300">{pro}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {analysis.cons?.length > 1 && (
                          <div>
                            <h4 className="font-semibold text-sm text-red-400 mb-2">All Cons</h4>
                            <ul className="space-y-1">
                              {analysis.cons.map((con, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm">
                                  <AlertCircle className="w-3 h-3 text-red-400 flex-shrink-0 mt-1" />
                                  <span className="text-gray-300">{con}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Select Button */}
                  <div className="p-4 border-t border-border">
                    <Button
                      onClick={() => handleSelectVendor(vendor.vendor_id, "manual")}
                      disabled={selecting || isSelected}
                      data-testid={`select-vendor-${vendor.vendor_id}`}
                      className={`w-full ${
                        isSelected 
                          ? "bg-accent-success/20 text-accent-success" 
                          : "bg-background-subtle hover:bg-primary-main/10 hover:text-primary-main"
                      }`}
                    >
                      <Hand className="w-4 h-4 mr-2" />
                      {isSelected ? "Selected" : "Select Vendor"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Comparison Table with Checkboxes */}
          <div className="glass rounded-xl overflow-hidden">
            <div className="p-6 border-b border-border">
              <h2 className="font-display text-2xl font-semibold">
                Line Item Comparison
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Use checkboxes to select items from different vendors. Green highlights indicate the lowest price.
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="comparison-table w-full">
                <thead>
                  <tr className="bg-background-subtle">
                    <th className="py-4 px-6 text-left font-mono text-xs uppercase tracking-wider text-gray-400 sticky left-0 bg-background-subtle z-10">
                      Item Name
                    </th>
                    <th className="py-4 px-6 text-center font-mono text-xs uppercase tracking-wider text-gray-400">
                      Qty
                    </th>
                    {vendors.map((vendor) => (
                      <th 
                        key={vendor.vendor_id}
                        className="py-4 px-6 text-left font-mono text-xs uppercase tracking-wider text-gray-400 min-w-[180px]"
                      >
                        <div className="flex flex-col gap-1">
                          <span>{vendor.name}</span>
                          <span className="text-[10px] text-gray-500 normal-case">Click to select</span>
                        </div>
                      </th>
                    ))}
                    <th className="py-4 px-6 text-left font-mono text-xs uppercase tracking-wider text-gray-400">
                      Best Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {matchedItems.length === 0 ? (
                    <tr>
                      <td colSpan={vendors.length + 3} className="py-12 text-center text-gray-400">
                        No line items to compare. Upload vendor documents with pricing details.
                      </td>
                    </tr>
                  ) : (
                    matchedItems.map((item, idx) => {
                      // Get quantity from first vendor that has it
                      const firstVendorData = Object.values(item.vendors)[0];
                      const quantity = firstVendorData?.quantity || 1;
                      const matchConfidence = item.match_confidence || 'high';
                      
                      return (
                        <tr 
                          key={idx} 
                          className={`border-b border-border hover:bg-background-subtle/50 transition-colors ${
                            matchConfidence === 'low' ? 'bg-amber-500/5' : ''
                          }`}
                        >
                          <td className="py-4 px-6 font-medium sticky left-0 bg-background z-10">
                            <div className="flex flex-col">
                              <span>{item.item_name}</span>
                              {matchConfidence !== 'high' && (
                                <span className={`text-xs mt-1 ${
                                  matchConfidence === 'medium' ? 'text-amber-400' : 'text-red-400'
                                }`} title={item.match_reason || 'Fuzzy matched'}>
                                  {matchConfidence === 'medium' ? '⚡ Likely match' : '⚠ Uncertain match'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-6 text-center font-mono text-gray-400">
                            {quantity}
                          </td>
                          {vendors.map((vendor) => {
                            const vendorData = item.vendors[vendor.vendor_id];
                            const price = vendorData?.unit_price || vendorData?.total_price;
                            const isLowest = vendor.vendor_id === item.lowest_vendor_id;
                            const isChecked = isItemSelected(item.item_name, vendor.vendor_id);
                            const hasPrice = price !== undefined && price !== null;
                            const vendorHasItem = vendorData !== undefined && vendorData !== null;
                            const originalName = vendorData?.original_name;
                            
                            return (
                              <td 
                                key={vendor.vendor_id}
                                className={`py-4 px-6 ${isLowest ? "bg-accent-success/10" : ""}`}
                              >
                                {vendorHasItem ? (
                                  <div className="flex items-center gap-3">
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={(checked) => toggleItemSelection(item.item_name, vendor.vendor_id, checked)}
                                      data-testid={`checkbox-${item.item_name}-${vendor.vendor_id}`}
                                      className={`border-gray-500 ${isChecked ? "bg-primary-main border-primary-main" : ""}`}
                                    />
                                    <div className="flex flex-col">
                                      <span className={`font-mono ${isLowest ? "text-accent-success font-bold" : "text-gray-300"}`}>
                                        {hasPrice ? `$${price.toLocaleString()}` : 'N/A'}
                                        {isLowest && hasPrice && (
                                          <span className="ml-2 text-xs px-1.5 py-0.5 bg-accent-success/20 rounded">
                                            LOWEST
                                          </span>
                                        )}
                                      </span>
                                      {originalName && originalName !== item.item_name && (
                                        <span className="text-xs text-gray-500 truncate max-w-[150px]" title={originalName}>
                                          "{originalName}"
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-600 italic">Not offered</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="py-4 px-6 text-accent-success font-medium">
                            {vendors.find(v => v.vendor_id === item.lowest_vendor_id)?.name || "-"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                
                {/* Summary Row */}
                {selectionSummary.itemCount > 0 && (
                  <tfoot>
                    <tr className="bg-primary-main/10 border-t-2 border-primary-main">
                      <td className="py-4 px-6 font-bold sticky left-0 bg-primary-main/10 z-10">
                        SELECTED TOTAL
                      </td>
                      <td className="py-4 px-6 text-center font-mono">
                        {selectionSummary.itemCount}
                      </td>
                      <td colSpan={vendors.length} className="py-4 px-6">
                        <span className="text-gray-400">
                          Items selected from {Object.keys(selectionSummary.itemsByVendor).length} vendor(s)
                        </span>
                      </td>
                      <td className="py-4 px-6 font-display text-2xl font-bold text-primary-main">
                        ${selectionSummary.totalPrice.toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="bg-background-paper border-border">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Export Comparison</DialogTitle>
            <DialogDescription className="text-gray-400">
              Choose your preferred export format
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <Button
              data-testid="export-xlsx-btn"
              onClick={() => handleExport("xlsx")}
              className="flex items-center justify-start gap-3 h-auto p-4 bg-background-subtle hover:bg-primary-main/10 hover:text-primary-main"
            >
              <FileSpreadsheet className="w-8 h-8 text-green-400" />
              <div className="text-left">
                <div className="font-semibold">Download Excel (.xlsx)</div>
                <div className="text-sm text-gray-400">Full comparison with AI analysis sheet</div>
              </div>
            </Button>
            
            <Button
              data-testid="export-csv-btn"
              onClick={() => handleExport("csv")}
              className="flex items-center justify-start gap-3 h-auto p-4 bg-background-subtle hover:bg-primary-main/10 hover:text-primary-main"
            >
              <FileText className="w-8 h-8 text-blue-400" />
              <div className="text-left">
                <div className="font-semibold">Download CSV</div>
                <div className="text-sm text-gray-400">Simple format for any spreadsheet app</div>
              </div>
            </Button>
            
            <Button
              data-testid="copy-excel-btn"
              onClick={copyToClipboard}
              className="flex items-center justify-start gap-3 h-auto p-4 bg-background-subtle hover:bg-primary-main/10 hover:text-primary-main"
            >
              <div className="w-8 h-8 flex items-center justify-center text-primary-main">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </div>
              <div className="text-left">
                <div className="font-semibold">Copy to Clipboard</div>
                <div className="text-sm text-gray-400">Paste directly into Excel or Google Sheets (includes selections)</div>
              </div>
            </Button>
            
            {selectionSummary.itemCount > 0 && (
              <Button
                data-testid="copy-selection-only-btn"
                onClick={exportSelection}
                className="flex items-center justify-start gap-3 h-auto p-4 bg-primary-main/10 border border-primary-main/30 hover:bg-primary-main/20"
              >
                <ShoppingCart className="w-8 h-8 text-primary-main" />
                <div className="text-left">
                  <div className="font-semibold text-primary-main">Copy Selection Only</div>
                  <div className="text-sm text-gray-400">
                    Export your {selectionSummary.itemCount} selected items (${selectionSummary.totalPrice.toLocaleString()})
                  </div>
                </div>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Vendor Modal */}
      <Dialog open={showAddVendorModal} onOpenChange={setShowAddVendorModal}>
        <DialogContent className="bg-background-paper border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Plus className="w-6 h-6 text-accent-success" />
              Add Another Vendor
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Upload an additional vendor quote to include in this comparison
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Vendor Name Input */}
            <div className="space-y-2">
              <Label htmlFor="vendorName" className="text-gray-300">Vendor Name</Label>
              <Input
                id="vendorName"
                placeholder="e.g., Delta Supplies"
                value={newVendorName}
                onChange={(e) => setNewVendorName(e.target.value)}
                data-testid="new-vendor-name-input"
                className="bg-background-subtle border-border focus:border-primary-main"
              />
            </div>

            {/* File Dropzone */}
            <div className="space-y-2">
              <Label className="text-gray-300">Vendor Quote File</Label>
              <div
                {...getRootProps()}
                data-testid="new-vendor-dropzone"
                className={`drop-zone rounded-xl p-8 text-center cursor-pointer transition-all ${
                  isDragActive ? "drag-over border-primary-main bg-primary-main/5" : ""
                } ${newVendorFile ? "border-accent-success bg-accent-success/5" : ""}`}
              >
                <input {...getInputProps()} />
                {newVendorFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-10 h-10 text-accent-success" />
                    <div className="text-left">
                      <p className="font-semibold text-accent-success">{newVendorFile.name}</p>
                      <p className="text-sm text-gray-500">{(newVendorFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewVendorFile(null);
                      }}
                      className="text-gray-500 hover:text-red-400"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-primary-main/50 mx-auto mb-3" />
                    <p className="text-gray-300 mb-1">
                      {isDragActive ? "Drop file here..." : "Drag & drop vendor file"}
                    </p>
                    <p className="text-sm text-gray-500">
                      PDF, Excel, CSV, Word, or Image
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Info Note */}
            <div className="flex items-start gap-3 p-3 bg-primary-main/5 border border-primary-main/20 rounded-lg">
              <Brain className="w-5 h-5 text-primary-main flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-400">
                <p>After adding the vendor, the AI will:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Extract all line items and pricing</li>
                  <li>Re-match items across all vendors</li>
                  <li>Update the comparison table</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddVendorModal(false);
                setNewVendorName("");
                setNewVendorFile(null);
              }}
              className="border-border hover:bg-background-subtle"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-add-vendor-btn"
              onClick={handleAddVendor}
              disabled={addingVendor || !newVendorFile || !newVendorName.trim()}
              className="bg-accent-success text-black font-bold hover:bg-accent-success/90"
            >
              {addingVendor ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding & Analyzing...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Vendor
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComparisonResults;
